import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { resetPassword } from '../../actions/passwordResets';
import './_styles_/ResetPasswordPopup.css';
import ModalDialog from '../ModalDialog';
import PasswordField from '../fields/PasswordField';
import useMultiValidator from '../../hooks/useMultiValidator';
import PasswordPolicyPopup from './PasswordPolicyPopup';
import { onActionKey } from '../../utils';
import {
    getPasswordPolicyFromToken,
    getUserIdFromToken,
} from '../../utils/authentication';
import {
    passwordValidator,
    repeatedPasswordValidator,
} from '../../validators/passwords';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ResetPasswordPopup.propTypes = {
    resetToken: PropTypes.string.isRequired,
    hideDialog: PropTypes.func.isRequired,
};

function ResetPasswordPopup(props) {
    const {
        resetToken,
        hideDialog,
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [isPasswordPolicyVisible, setPasswordPolicyVisible] = useState(false);
    const passwordPolicy = getPasswordPolicyFromToken(resetToken);
    const userId = getUserIdFromToken(resetToken);
    const onSubmit = (event) => {
        dispatch(resetPassword(resetToken, newPassword, repeatPassword));
        setNewPassword('');
        setRepeatPassword('');
        event.preventDefault();
    };
    const showPasswordPolicy = () => setPasswordPolicyVisible(true);
    const hidePasswordPolicy = () => setPasswordPolicyVisible(false);
    const { validators, isValid } = useMultiValidator(false, {
        password: compositeValidator(passwordValidator(t, passwordPolicy, userId), wafValidator(t)),
        repeatedPassword: repeatedPasswordValidator(t, newPassword),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    return (
        <>
            <ModalDialog onCancel={hideDialog}>
                <div className="reset-password-popup-dialog">
                    <h4>{t('reset-password.title')}</h4>
                    <form className="reset-password" onSubmit={onSubmit}>
                        <div className="reset-password-input-row">
                            <PasswordField
                                label={t('reset-password.password')}
                                inputId="reset-password-new-password"
                                className="reset-password--field--password"
                                value={newPassword}
                                validator={validators.password}
                                onChange={setNewPassword}
                                placeholder={t('reset-password.placeholder.new-password')}
                                autoComplete="new-password"
                                showPasswordStrength
                            />
                        </div>
                        <div className="reset-password-input-row">
                            <PasswordField
                                label={t('reset-password.repeated-password')}
                                inputId="reset-password-repeat-password"
                                className="reset-password--field--repeat-password"
                                value={repeatPassword}
                                validator={validators.repeatedPassword}
                                onChange={setRepeatPassword}
                                placeholder={t('reset-password.placeholder.repeat-password')}
                                autoComplete="new-password"
                            />
                        </div>
                        <span
                            className="password-policy-link"
                            onClick={showPasswordPolicy}
                            onKeyUp={onActionKey(showPasswordPolicy)}
                            role="button"
                            tabIndex={0}
                        >
                            {t('passwords.show-policy')}
                        </span>
                        <div className="buttons">
                            <button disabled={isDisabled} className="button button-bordered reset-password-button" type="submit">{t('button.save')}</button>
                            <button className="button button-bordered back-button" type="button" onClick={hideDialog}>{t('button.cancel')}</button>
                        </div>
                    </form>
                </div>
            </ModalDialog>
            {isPasswordPolicyVisible && <PasswordPolicyPopup passwordPolicy={passwordPolicy} hideDialog={hidePasswordPolicy} />}
        </>
    );
}

export default ResetPasswordPopup;
